/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  getPaginationRowModel,
  getFilteredRowModel,
} from "@tanstack/react-table";
import {
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import dataService from "service/dataService";
import InputField from "components/fields/InputField";
import CurrencyInput from "react-currency-input-field";
import ConfirmationModal from "./ConfirmationModal";

interface StudentData {
  // Example fields, replace with actual fields as needed
  id: string;
  uid: string;
  firstName: string;
  lastName: string;
  grade: string;
  hourlyRate: string;
  major: string;
  email: string;
  phoneNumber: string;
  uniqueData: boolean | false; // For any additional unique data if needed
  discount:number;
}

const ViewStudentData: React.FC = () => {
  const [data, setData] = useState<StudentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initialData: StudentData[] = [];
  const [globalFilter, setGlobalFilter] = useState("");
  const [editingRow, setEditingRow] = useState<StudentData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  let uniqueData = false;
  useEffect(() => {
    fetchData();
  }, []);

    const fetchData = async () => {
      try {
        setLoading(true);
        dataService.fetchStudentData().then((students) => {
          students.forEach((student: any) => {
            console.log("Fetched student:", student);
            if (
              student.uid === undefined ||
              student.uid === null ||
              student.uid === ""
            ) {
              uniqueData = false;
            } else {
              uniqueData = true;
            }
            initialData.push({
              id: student.id,
              uid: student.id,
              firstName: student.firstName || "",
              lastName: student.lastName || "",
              grade: student.grade || "",
              hourlyRate: student.hourlyRate || "",
              major: student.major || "",
              email: student.email || "",
              phoneNumber: student.phoneNumber || "",
              uniqueData: uniqueData,
              discount: student.discount
            });
            console.log("Initial data array:", initialData);
          });

          setData(initialData);
        });
      } catch (err) {
        setError("Failed to fetch data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

  const columns: ColumnDef<StudentData>[] = [
    { accessorKey: "#", header: "#" },
    //{ accessorKey: 'id', header: 'ID' },
    { accessorKey: "firstName", header: "FirstName" },
    { accessorKey: "lastName", header: "lastName" },
    { accessorKey: "grade", header: "Grade" },
    { accessorKey: "hourlyRate", header: "Hourly Rate" },
    { accessorKey: "major", header: "Major" },
    { accessorKey: "discount", header: "Discount %" },
    { accessorKey: "action", header: "Action" },
    //  { accessorKey: 'uniqueData', header: 'uniqueData' },
  ];
  let rowId = React.useRef(9);
  const createUniqueId = () => rowId.current++;
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    state: {
      globalFilter,
    },
  });

  const cloneRow = (originalRow: StudentData, originalIndex: number) => {
    // Create a deep copy of the row data to avoid mutation
    const orgId = originalRow.id;
    const newRow = { ...originalRow, id: createUniqueId().toString() };
    const newData = [...data];
    newRow.uid = orgId;
    newRow.hourlyRate = "";
    newRow.major = "";
    newData.splice(originalIndex + 1, 0, newRow); // Insert the new row after the original
    setData(newData);
    setEditingRow(newRow);
  };

  const [showAlert, setShowAlert] = useState(false);
  const handleSave = (originalRow: StudentData) => {
    if (!editingRow) return;
    const updatedData = data.map((row) =>
      row.id === originalRow.id ? { ...editingRow } : row
    );
    console.log("Updated Data:", updatedData);
    dataService.addStudentData({
      uid: originalRow.uid,
      firstName: originalRow.firstName,
      lastName: originalRow.lastName,
      grade: originalRow.grade,
      hourlyRate: editingRow.hourlyRate,
      major: editingRow.major,
      email: originalRow.email,
      phoneNumber: originalRow.phoneNumber,
      fullName: `${originalRow.firstName} ${originalRow.lastName}`,
      discount: 0,
    });
    uniqueData = false;
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
    setData(updatedData);
    setEditingRow(null);
    fetchData();
  };

  const handleCancel = () => {
    setData((prevData) => prevData.filter((row) => row.id !== editingRow?.id));
    setEditingRow(null);
  };

  const handleDelete = (orgId:string) => {
    setItemToDelete(orgId);
    setIsModalOpen(true);
  }

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleConfirmAction = async () => {
    // Perform the action after confirmation
    console.log('Action confirmed!' + itemToDelete);
    await dataService.deleteRow('students',itemToDelete);
    // After deletion, update local state to reflect the change
    setData(prevItems => prevItems.filter(item => item.id !== itemToDelete));

    setIsModalOpen(false); // Close modal after action
  };

  if (loading) return <p>Loading data...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <div className="bg-gray-50 p-4 dark:bg-gray-900 ">
     {/* Success Alert */}
      { showAlert && (
        <div className=" mx-auto mt-1 p-2 bg-white shadow-lg rounded-lg">
      <div id="alert-border-1" className="max-w-6xl flex items-center p-4 mb-4 text-green-800 border-t-4 border-green-300 bg-green-50 dark:text-green-400 dark:bg-gray-800 dark:border-green-800" role="alert">
      <svg className="shrink-0 w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
        <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"/>
      </svg>
      <div className="ms-3 text-sm font-medium">
          New Major and Hourly Rate added successfully
        </div>
      </div>
          </div>
      )}
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="mx-auto max-w-6xl rounded-lg bg-white p-6 shadow-md dark:bg-gray-800">
          {/* Search Bar */}
          <div className="relative mb-4">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>
            <input
              type="text"
              value={globalFilter ?? ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
              placeholder="Search all columns..."
            />
          </div>

          {/* Table*/}
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {table.getRowModel().rows.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`${
                      editingRow?.id === row.original.id ? "bg-blue-100" : ""
                    }`}
                  >
                    {row.getVisibleCells().map((cell) => {
                      if (
                        editingRow?.id === row.original.id &&
                        (cell.column.id === "hourlyRate" ||
                          cell.column.id === "major")
                      ) {
                        return (
                          <td
                            key={cell.id}
                            className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-gray-200"
                          >
                            {cell.column.id === "hourlyRate" ? (
                              <CurrencyInput
                                id="hourlyRate"
                                name="hourlyRate"
                                className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                                placeholder="Eg: $45.00"
                                defaultValue={
                                  editingRow.hourlyRate
                                    ? parseFloat(editingRow.hourlyRate)
                                    : undefined
                                }
                                decimalsLimit={2} // Limit to 2 decimal places
                                prefix="$" // Add a currency prefix
                                intlConfig={{
                                  locale: "en-US",
                                  currency: "USD",
                                }} // Specify locale and currency
                                onValueChange={(value: string | undefined) =>
                                  setEditingRow({
                                    ...editingRow,
                                    hourlyRate: value || "",
                                  })
                                }
                                required
                              />
                            ) : cell.column.id === "major" ? (
                              <InputField
                                type="text"
                                value={editingRow.major}
                                onchange={(e) =>
                                  setEditingRow({
                                    ...editingRow,
                                    major: e.target.value,
                                  })
                                }
                                id="major"
                                label=""
                                extra=""
                                placeholder="Eg:SAT"
                                variant=""
                                className="mt-1"
                              />
                            ) : null}
                          </td>
                          
                        );
                      }
                      
                      return (
                        <td
                          key={cell.id}
                          className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-gray-200"
                        >
                          {cell.column.id === "#" &&
                          editingRow?.id === row.original.id ? (
                            <span className="text-gray-500">#</span>
                          ) : cell.column.id === "#" ? (
                            <span className="text-gray-500">{index + 1}</span>
                          ) : null}
                          {cell.column.id === "action" ? (
                            <div className="flex items-center space-x-2">
                              {editingRow?.id === row.original.id ? (
                                <>
                                  <button
                                    onClick={() => handleSave(row.original)}
                                    type="button"
                                    className="rounded-lg bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 px-3 py-2 text-center text-xs font-medium font-medium text-white shadow-lg shadow-blue-500/50 hover:bg-gradient-to-br focus:outline-none focus:ring-4 focus:ring-blue-300 dark:shadow-lg dark:shadow-blue-800/80 dark:focus:ring-blue-800"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancel}
                                    type="button"
                                    className="rounded-lg bg-gradient-to-r from-red-400 via-red-500 to-red-600 px-3 py-2 text-xs font-medium text-white shadow-lg shadow-red-500/50 hover:bg-gradient-to-br focus:outline-none focus:ring-4 focus:ring-red-300 dark:shadow-lg dark:shadow-red-800/80 dark:focus:ring-red-800"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                 <>
                                <button
                                   onClick={() => (data[index].uniqueData === false ? cloneRow(row.original, index) : '')}
                                  type="button"
                                  className={
                                    data[index].uniqueData === false
                                      ? "rounded-lg bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 px-3 py-2 text-center text-xs font-medium font-medium text-white shadow-lg shadow-blue-500/50 hover:bg-gradient-to-br focus:outline-none focus:ring-4 focus:ring-blue-300 dark:shadow-lg dark:shadow-blue-800/80 dark:focus:ring-blue-800"
                                      : "cursor-not-allowed rounded-lg rounded-lg bg-gray-500 px-3 py-2 text-center text-xs font-medium font-medium text-white dark:bg-gray-300 dark:shadow-lg dark:shadow-blue-800/80"
                                  }
                                  // className="px-3 py-2 text-xs font-medium text-center text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 shadow-lg shadow-blue-500/50 dark:shadow-lg dark:shadow-blue-800/80 font-medium rounded-lg"
                                >
                                  Clone 
                                </button>

                               {/* { data[index].uniqueData !== true && */}
                                <button
                                  onClick={() => handleDelete(data[index].id)}
                                  type="button"
                                  className={                         
                                      "rounded-lg bg-gradient-to-r from-red-500 via-red-600 to-red-700 px-3 py-2 text-center text-xs font-medium font-medium text-white shadow-lg shadow-red-500/50 hover:bg-gradient-to-br focus:outline-none focus:ring-4 focus:ring-red-300 dark:shadow-lg dark:shadow-red-800/80 dark:focus:ring-red-800"
                                  }
                                  // className="px-3 py-2 text-xs font-medium text-center text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 shadow-lg shadow-blue-500/50 dark:shadow-lg dark:shadow-blue-800/80 font-medium rounded-lg"
                                >
                                  Delete
                                </button>

                                 {/* { data[index].uniqueData !== true && */}
                            <ConfirmationModal
                              isOpen={isModalOpen}
                              onClose={handleCloseModal}
                              title="Confirm Deletion"
                              onConfirm={handleConfirmAction}
                              confirmText=" Yes, I'm sure"
                              cancelText="No, cancel"
                            >
                         
                           <h3 className="mb-5 mt-3 text-lg font-normal text-gray-500 dark:text-gray-400">Are you sure you want to delete Student?</h3>
                           </ConfirmationModal>
                             </>
                          )}
                              
                           </div>
                          ) : cell.column.id !== "id" ? (
                             flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext() 
                            )
                          ) : 'null'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-gray-700 dark:text-gray-400">
              Page{" "}
              <strong>
                {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount()}
              </strong>
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="rounded-md border bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="rounded-md border bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
              >
                <ArrowRightIcon className="h-5 w-5" />
              </button>

              <div className="flex items-center gap-x-2">
                <select
                  className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => {
                    table.setPageSize(Number(e.target.value));
                  }}
                >
                  {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewStudentData;
